import React, { useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';

import { Icon } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { SectionHeader } from '~/components';
import { PaginatedDataTable, type PaginatedDataTableRef, useTimeFormat } from '~/components/Table';
import { useNavigate } from '~/lib/navigation';
import { Inner } from '~/styles';

import { LoanProps } from './Loan';
import { getPaymentColumns, handleFetchPayments, paymentFilter } from './Payment/Overview';

const LoanPaymentsTable: React.FC<{ loanId: string }> = ({ loanId }) => {
  const navigate = useNavigate();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('loanPayments');
  const tableRef = useRef<PaginatedDataTableRef>(null);
  const loanIdRef = useRef(loanId);

  useEffect(() => {
    if (loanIdRef.current === loanId) {
      return;
    }
    loanIdRef.current = loanId;
    tableRef.current?.refetch();
  }, [loanId]);

  return (
    <Inner p={0} pb={24}>
      <PaginatedDataTable<Record<string, any>>
        ref={tableRef}
        tableId="loanPayments"
        fetch={(params) => handleFetchPayments({ ...params, loanId }, navigate)}
        filter={paymentFilter}
        empty={{
          icon: <Icon.AnimationHandCoins />,
          title: 'No payments found',
          description: 'No payments were found for this loan.',
        }}
        columns={getPaymentColumns(timeFormat, onTimeFormatChange)}
        onRowClick={(row) => {
          navigate(`${ROUTE.LOANS}/payments/edit/${row.id}`);
        }}
      />
    </Inner>
  );
};

export const PageLoanPayments: React.FC = () => {
  const { loan } = useOutletContext<LoanProps>();

  if (!loan?.id) {
    return null;
  }

  return (
    <>
      <SectionHeader text="Payments" />
      <LoanPaymentsTable loanId={loan.id} />
    </>
  );
};
